import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import Calendar from 'react-calendar';

import T, { Translate } from '../i18n/i18n';
import * as Tag from './TableDataForm.styles';
import TimeSelector from '../Forms/TimeSelector/TimeSelector';
import ResourceImage from '../ResourceImage/ResourceImage';
import TagSelector from '../Forms/TagSelector/TagSelector';
import { filemanagerActions, editTableActions } from '../../redux/actions';
import { ConvertToDate } from '../../services/dates';

const initialDataState = {
    name: '',
    thumbnail: '',
    charsheetTemplate: {},
    next: null,
    frequency: '',
    start: '',
    end: '',
    positions: '',
    public: '0',
    description: '',
    content: [],
    tools: [],
};

export default ({padding, onChange, value, error}) => {

    const dispatch = useDispatch();

    const [data, setData] = useState(initialDataState);

    const changeField = (field, value) => {
        setData(d => ({...d, [field]: value}));

        if (onChange && typeof onChange === 'function') {
            onChange({...data, [field]: value});
        }
    };

    const converToNumber = (v) => {
        return String(parseInt(v.replace(/[^\d]/gi,"") || '0') || 0);
    };

    const handleChangeImage = () => {
        dispatch(filemanagerActions.openWindow('single', async (result) => {
            if (result && result[0] && result[0].id) {
                changeField('thumbnail', result[0].id);
            }
        }));
    }

    const handleOpenTemplate = () => {
        dispatch(editTableActions.openChooseTemplateWindow((result) => {
            changeField('charsheetTemplate', result)
        }));
    };

    useEffect(() => {
        setData(d => {
            let result = {...d, ...value};

            if (typeof result["next"] === 'string') {
                result["next"] = ConvertToDate(result["next"]);
            }
            if (typeof result["public"] === 'boolean') {
                result["public"] = result["public"] ? "1" : "0";
            }

            return result;
        });
    }, [value]);
    
    return <Tag.Form padding={padding}>
        {error ? <Tag.Error>{error}</Tag.Error> : null}
        <Tag.Row align="flex-start">
            <Tag.Image onClick={handleChangeImage}>
                {data.thumbnail ? <ResourceImage resourceId={data.thumbnail} /> : <Tag.ImageIcon />}
            </Tag.Image>
            <Tag.Column style={{width:"380px"}}>
                <Tag.Field>
                    <Tag.Label><T value="tableform.name" /></Tag.Label>
                    <Tag.Input type="text" value={data.name ?? ''} onChange={(e)=>changeField('name', e.target.value)} maxLength={40} style={{width:'250px'}}/>
                </Tag.Field>
                <Tag.Field className="noBottom">
                    <Tag.Label><T value="tableform.charsheet" /></Tag.Label>
                    <Tag.Row>
                        <Tag.Button color="#28f" onClick={handleOpenTemplate}><T value="tableform.choose" /></Tag.Button>
                        {data.charsheetTemplate ? 
                            <Tag.Value>{data.charsheetTemplate.name ?? ''}</Tag.Value> 
                        : 
                            <Tag.NoValue><T value="tableform.charsheet-none" /></Tag.NoValue> }
                    </Tag.Row>
                </Tag.Field>
            </Tag.Column>
        </Tag.Row>

        <Tag.Row align="flex-start">
            <Tag.Column style={{flex:5}}>
                <Tag.Row align="stretch">
                    <Tag.Field style={{flex:2}}>
                        <Tag.Label><T value="tableform.next" /></Tag.Label>
                        <Tag.Date className="formCalendar" value={data.next} onChange={(e)=>changeField('next', e.target.value)}>
                            <Calendar 
                                minDetail="year" 
                                locale={Translate('locale')}
                                next2Label={null}
                                prev2Label={null}
                                value={data.next}
                                onChange={(v)=>changeField('next', v)}
                            />
                        </Tag.Date>
                    </Tag.Field>
                    <Tag.Field style={{flex:3}}>
                        <Tag.Label><T value="tableform.description" /></Tag.Label>
                        <Tag.TextArea value={data.description ?? ''} onChange={(e)=>changeField('description', e.target.value)} />
                    </Tag.Field>
                </Tag.Row>
                <Tag.Row align="flex-start">
                    <Tag.Field style={{flex:2}}>
                        <Tag.Label><T value="tableform.frequency" /></Tag.Label>
                        <Tag.Select value={data.frequency ?? ''} onChange={(e)=>changeField('frequency', e.target.value)} >
                            <option value=""></option>
                            <option value="weekly">{Translate("tableform.frequency-weekly")}</option>
                            <option value="fortnight">{Translate("tableform.frequency-fortnight")}</option>
                            <option value="monthly">{Translate("tableform.frequency-monthly")}</option>
                            <option value="oneshot">{Translate("tableform.frequency-oneshot")}</option>
                        </Tag.Select>
                    </Tag.Field>
                    <Tag.Row style={{flex:3}}>
                        <Tag.Field>
                            <Tag.Label><T value="tableform.start" /></Tag.Label>
                            <TimeSelector value={data.start ?? ''} onChange={(v)=>changeField('start', v)} />
                        </Tag.Field>
                        <Tag.Field>
                            <Tag.Label><T value="tableform.end" /></Tag.Label>
                            <TimeSelector value={data.end ?? ''} onChange={(v)=>changeField('end', v)} />
                        </Tag.Field>
                        <Tag.Field>
                            <Tag.Label><T value="tableform.positions" /></Tag.Label>
                            <Tag.Input type="text" value={data.positions ?? ''} onChange={(e)=>changeField('positions', converToNumber(e.target.value))} style={{width: '60px'}}/>
                        </Tag.Field>
                        <Tag.Field style={{width: '100%'}}>
                            <Tag.Label><T value="tableform.public" /></Tag.Label>
                            <Tag.Select  value={data.public ?? '0'} onChange={(e)=>changeField('public', e.target.value)} style={{width: '100%'}}>
                                <option value="0">{Translate("no")}</option>
                                <option value="1">{Translate("yes")}</option>
                            </Tag.Select>
                        </Tag.Field>
                    </Tag.Row>
                </Tag.Row>
            </Tag.Column>

            <Tag.Column style={{flex:3}}>
                <TagSelector 
                    includedColor="#56B431"
                    canExclude={false}
                    canAdd={true}
                    hideSubtitles={true}
                    tagGroup="contentFilter"
                    tagService="content"
                    onChange={(v)=>changeField('content', v)}
                    value={data.content}
                />
                <TagSelector 
                    includedColor="#FFB800"
                    canExclude={false}
                    canAdd={true}
                    hideSubtitles={true}
                    tagGroup="tools"
                    tagService="tools"
                    onChange={(v)=>changeField('tools', v)}
                    value={data.tools}
                />
            </Tag.Column>
        </Tag.Row>

    </Tag.Form>
};